import {Component, OnInit} from '@angular/core';
import {CommonModule, formatDate} from '@angular/common';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {DatePickerModule} from 'primeng/datepicker';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faAngleLeft, faAngleRight, faPen} from '@fortawesome/free-solid-svg-icons';
import {environment} from '../../../../environments/environment';

import {EntryService} from '../../../services/entry.service';
import {RegisterService} from '../../../services/register.service';
import {AuthService} from '../../../services/auth.service';
import {GenericSearchComponent} from '../../../shared/search-suggestion/search-suggestion.component';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../../../shared/constants/pagination.constants';
import {VtePaginationComponent} from '../../../shared/vte/vte-pagination/vte-pagination.component';

@Component({
    selector: 'app-entry-list',
    imports: [CommonModule, FormsModule, ReactiveFormsModule, DatePickerModule, FontAwesomeModule, GenericSearchComponent, VtePaginationComponent],
    templateUrl: './entry-list.component.html',
    styleUrl: './entry-list.component.css'
})
export class EntryListComponent implements OnInit {
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;
  entries: any[] = [];
  registers: any[] = [];
  locations: any[] = [];

  recordsPerPage: number = 10;
  currentPage: number = 1;
  totalRecords: number = 0;
  nextPage: string | null = null;
  previousPage: string | null = null;
  searchText: string = '';

  filtersForm: FormGroup;
  isAdminUser: boolean = false;
  searchExtras: Record<string, any> = {};
  visitorApiBaseUrl: string = environment.baseUrl.replace('/v1/', '/api/v1/');
  locationSearch: string = '';
  locationPage: number = 1;
  locationsPageSize: number = 200;
  hasMoreLocations: boolean = false;
  private locationSearchTimer: any = null;

  constructor(
    private entryService: EntryService,
    private registerService: RegisterService,
    private auth: AuthService,
    private fb: FormBuilder,
    private router: Router,
    library: FaIconLibrary
  ) {
    library.addIcons(faAngleLeft, faAngleRight, faPen);
    this.filtersForm = this.fb.group({
      register_id: [''],
      location_id: [''],
      status: [''],
      fromDate: [null],
      toDate: [null]
    });
  }

  ngOnInit(): void {
    const roles = this.getUserRoles();
      const isPlatformSuperuser = this.auth.isSuperUser();
      this.isAdminUser = roles.includes('Admin') && !isPlatformSuperuser;
      if (isPlatformSuperuser) {
      this.router.navigate(['/dashboard']);
      return;
    }
    if (!this.isAdminUser) {
      this.router.navigate(['/visitor-management/select-register']);
      return;
    }
    this.loadRegisters();
    this.loadLocations();
    this.updateSearchExtras();
    this.fetchEntries(this.currentPage, '');
  }

  private getUserRoles(): string[] {
    const userInfo = this.auth.getCurrentUserInfo();
    const role = userInfo?.role;
    if (Array.isArray(role)) {
      return role;
    }
    return role ? [role] : [];
  }

  loadRegisters(): void {
    this.registerService.getRegisters(1, 200).subscribe({
      next: (res: any) => {
        this.registers = res?.results || [];
      }
    });
  }

  loadLocations(page: number = 1, append: boolean = false): void {
    this.registerService.listLocations(page, this.locationsPageSize, this.locationSearch).subscribe({
      next: (res: any) => {
        const results = res?.results ?? res ?? [];
        this.locations = append ? [...this.locations, ...results] : results;
        this.hasMoreLocations = !!res?.next;
        this.locationPage = page;
      }
    });
  }

  onLocationSearch(term: string): void {
    this.locationSearch = term;
    if (this.locationSearchTimer) {
      clearTimeout(this.locationSearchTimer);
    }
    this.locationSearchTimer = setTimeout(() => {
      this.loadLocations(1, false);
    }, 300);
  }

  loadMoreLocations(): void {
    if (!this.hasMoreLocations) {
      return;
    }
    this.loadLocations(this.locationPage + 1, true);
  }

  fetchEntries(page: number, searchText: string): void {
    const filters = this.filtersForm.value;
    const params: any = {
      page,
      page_size: this.recordsPerPage,
      register_id: filters.register_id || '',
      location_id: filters.location_id || '',
      status: filters.status || ''
    };
    if (filters.fromDate) {
      params.start_date = formatDate(filters.fromDate, 'yyyy-MM-dd', 'en-US');
    }
    if (filters.toDate) {
      params.end_date = formatDate(filters.toDate, 'yyyy-MM-dd', 'en-US');
    }
    if (searchText) {
      params.search = searchText;
    }
    this.entryService.getEntries(params).subscribe({
      next: (res: any) => {
        this.entries = res?.results || [];
        this.totalRecords = res?.count ?? 0;
        this.nextPage = res?.next ?? null;
        this.previousPage = res?.previous ?? null;
      }
    });
  }

  onResults(event: any): void {
    this.searchText = event.searchText || '';
    this.entries = event.results || [];
    this.totalRecords = event.totalRecords || 0;
    this.nextPage = event.nextPage || null;
    this.previousPage = event.previousPage || null;
    this.currentPage = 1;
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.updateSearchExtras();
    this.fetchEntries(this.currentPage, '');
  }

  updateSearchExtras(): void {
    const filters = this.filtersForm.value;
    this.searchExtras = {
      register_id: filters.register_id || '',
      location_id: filters.location_id || '',
      status: filters.status || '',
      start_date: filters.fromDate ? formatDate(filters.fromDate, 'yyyy-MM-dd', 'en-US') : '',
      end_date: filters.toDate ? formatDate(filters.toDate, 'yyyy-MM-dd', 'en-US') : ''
    };
  }

  editEntry(entryId: number): void {
    this.router.navigate([`/visitor-management/entry/${entryId}/edit`]);
  }

  next(): void {
    if (this.nextPage) {
      this.currentPage += 1;
      this.fetchEntries(this.currentPage, this.searchText);
    }
  }

  previous(): void {
    if (this.previousPage) {
      this.currentPage -= 1;
      this.fetchEntries(this.currentPage, this.searchText);
    }
  }

  selectPageSize(): void {
    this.currentPage = 1;
    this.fetchEntries(this.currentPage, this.searchText);
  }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.fetchEntries(this.currentPage, this.searchText);
    }

    onPageSizeChange(pageSize: number): void {
        this.recordsPerPage = pageSize;
        this.currentPage = 1;
        this.fetchEntries(this.currentPage, this.searchText);
    }

  get totalPages(): number {
    const totalPage = Math.ceil(this.totalRecords / this.recordsPerPage);
    return totalPage === 0 ? 1 : totalPage;
  }

  getRegisterName(registerId: number): string {
    const resolvedId = (registerId as any)?.id || registerId;
    const register = this.registers.find(item => item.id === resolvedId);
    return register ? register.name : '-';
  }

  getLocationLabel(locationId: number | null): string {
    const resolvedId = (locationId as any)?.id || locationId;
    if (!resolvedId) {
      return 'Org';
    }
    const loc = this.locations.find(item => item.id === resolvedId);
    if (!loc) {
      return '-';
    }
    return `${loc.village_or_locality_name}, ${loc.district_name}`;
  }
}
